import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Beef, User, LogOut, Home, Calendar, TrendingDown, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function PointsHistoryPage({ user, onLogout }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupedHistory, setGroupedHistory] = useState({});

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/admin/points-history`, getAuthHeaders());
      setHistory(response.data);
      
      // Group by month
      const grouped = response.data.reduce((acc, item) => {
        const month = item.reset_month;
        if (!acc[month]) {
          acc[month] = [];
        }
        acc[month].push(item);
        return acc;
      }, {});
      
      setGroupedHistory(grouped);
    } catch (error) {
      toast.error('Errore nel caricamento dello storico');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Cliente', 'Email', 'Punti Annullati', 'Data Reset', 'Mese'].join(','),
      ...history.map(item => [
        item.customer_name,
        item.customer_email,
        item.points_cancelled,
        new Date(item.reset_date).toLocaleDateString('it-IT'),
        item.reset_month
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `storico-punti-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('CSV esportato!');
  };

  const getTotalPointsCancelled = (monthHistory) => {
    return monthHistory.reduce((sum, item) => sum + item.points_cancelled, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <div className="text-[#5D1822] text-xl">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="points-history">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2 text-[#5D1822] hover:text-[#3A0F15] transition-colors">
              <Home size={20} />
              <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <div className="h-6 w-px bg-[#E5E5E5]"></div>
            <div className="flex items-center gap-3">
              <Beef className="w-8 h-8 text-[#5D1822]" />
              <h1 className="text-2xl font-serif font-bold text-[#5D1822]">Macelleria Borgese</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/profile" className="text-[#5A5A5A] hover:text-[#5D1822]">
              <User size={24} />
            </Link>
            <button onClick={onLogout} className="text-[#5A5A5A] hover:text-[#5D1822]">
              <LogOut size={24} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#5D1822] mb-2">
              Storico Punti Annullati
            </h2>
            <p className="text-[#5A5A5A]">Visualizza tutti i punti scaduti al primo del mese</p>
          </div>
          {history.length > 0 && (
            <button
              onClick={exportToCSV}
              className="btn-primary flex items-center gap-2"
              data-testid="export-csv-btn"
            >
              <Download size={20} />
              Esporta CSV
            </button>
          )}
        </div>

        {/* Summary */}
        {history.length > 0 && (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 card-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-full flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-[#FF6B35]" />
                </div>
                <div>
                  <p className="text-sm text-[#5A5A5A]">Totale Punti Annullati</p>
                  <p className="text-2xl font-bold text-[#FF6B35]">
                    {history.reduce((sum, item) => sum + item.points_cancelled, 0)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 card-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-[#5D1822]/10 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-[#5D1822]" />
                </div>
                <div>
                  <p className="text-sm text-[#5A5A5A]">Clienti Coinvolti</p>
                  <p className="text-2xl font-bold text-[#5D1822]">
                    {new Set(history.map(h => h.user_id)).size}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 card-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-[#C5A059]/10 rounded-full flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-[#C5A059]" />
                </div>
                <div>
                  <p className="text-sm text-[#5A5A5A]">Mesi Registrati</p>
                  <p className="text-2xl font-bold text-[#C5A059]">
                    {Object.keys(groupedHistory).length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* History by Month */}
        {Object.keys(groupedHistory).length === 0 ? (
          <div className="bg-white rounded-xl p-12 card-shadow text-center">
            <Calendar className="w-16 h-16 text-[#E5E5E5] mx-auto mb-4" />
            <p className="text-[#5A5A5A]">Nessuno storico disponibile</p>
            <p className="text-sm text-[#5A5A5A] mt-2">I punti annullati appariranno qui dopo il primo del mese</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedHistory).map(([month, items]) => (
              <div key={month} className="bg-white rounded-xl card-shadow overflow-hidden">
                <div className="bg-gradient-to-r from-[#5D1822] to-[#3A0F15] text-white px-6 py-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-serif font-bold">{month}</h3>
                    <div className="text-right">
                      <p className="text-sm text-white/70">Punti Totali Annullati</p>
                      <p className="text-2xl font-bold">{getTotalPointsCancelled(items)}</p>
                    </div>
                  </div>
                </div>
                <div className="divide-y divide-[#E5E5E5]">
                  {items.map((item) => (
                    <div key={item.id} className="p-6 hover:bg-[#F9F7F2] transition-colors">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-semibold text-[#2C2C2C] mb-1">{item.customer_name}</h4>
                          <p className="text-sm text-[#5A5A5A]">{item.customer_email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-[#FF6B35]">-{item.points_cancelled}</p>
                          <p className="text-sm text-[#5A5A5A]">
                            {new Date(item.reset_date).toLocaleDateString('it-IT', {
                              day: 'numeric',
                              month: 'long',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
